import React, {useEffect, useRef, useState} from "react";
import SignatureCanvas from "react-signature-canvas";
import Modal, {ModalBody, ModalHeader} from "../../bootstrap/Modal";
import {CardLabel, CardTitle} from "../../bootstrap/Card";
import FormGroup from "../../bootstrap/forms/FormGroup";
import Input from "../../bootstrap/forms/Input";
import Select from "../../bootstrap/forms/Select";
import Option from "../../bootstrap/Option";
import Button from "../../bootstrap/Button";
import Icon from "../../icon/Icon";
import {useFormik} from "formik";
import {onboardingStepTwoValidation} from "../../../validator/onboarding/onboardingValidation";
import jwtInstance from "../../../guards/jwt.interceptor";
import {API_USER} from "../../../utils/restApi";
import {errorResponse, successResponse} from "../../../utils/getResponse";
import {Viewer, Worker} from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

interface IOnboardingWizardModalProps {
    isOpen: boolean;
    onComplete: () => void;
    userData?: any;
    apiEndpoint?: string;
}

const ErrorMessage = ({formik, field}: { formik: any, field: string }) => {
    if (formik.touched[field] && formik.errors[field]) {
        return <div className='text-danger' style={{fontSize: '0.75rem', marginTop: '0.25rem'}}>{formik.errors[field]}</div>;
    }
    return null;
};

const OnboardingWizardModal = ({isOpen, onComplete, userData, apiEndpoint = API_USER.onboarding}: IOnboardingWizardModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
    const [step, setStep] = useState(1);
    const [signatureError, setSignatureError] = useState('');
    const sigCanvasRef = useRef<SignatureCanvas>(null);

    useEffect(() => {
        return () => {
            if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
        };
    }, [pdfObjectUrl]);

    const handlePreview = () => {
        if (pdfObjectUrl) {
            setShowPdfPreview(true);
            return;
        }
        setIsPreviewLoading(true);
        jwtInstance.get(API_USER.anexa6Preview, {responseType: 'blob'})
            .then(response => {
                const blob = new Blob([response.data], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);
                setPdfObjectUrl(url);
                setShowPdfPreview(true);
            })
            .catch(() => {})
            .finally(() => setIsPreviewLoading(false));
    };

    const formik = useFormik<Record<string, string>>({
        enableReinitialize: true,
        initialValues: {
            lastName: userData?.lastName || '',
            firstName: userData?.firstName || '',
            domicile: '',
            street: '',
            number: '',
            block: '',
            staircase: '',
            floor: '',
            apartment: '',
            sector: '',
            birthDate: userData?.dateOfBirth || '',
            identityDocType: '',
            identitySeries: '',
            identityNumber: '',
            cnp: userData?.cnp || '',
            email: userData?.email || '',
            phone: userData?.phoneNumber || '',
        },
        validate: onboardingStepTwoValidation,
        onSubmit: () => {},
    });

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSignatureError('');
            sigCanvasRef.current?.clear();
        }
    }, [isOpen]);

    const handleGoToSignature = async () => {
        await formik.setTouched({
            lastName: true,
            firstName: true,
            domicile: true,
            street: true,
            number: true,
            block: true,
            staircase: true,
            floor: true,
            apartment: true,
            sector: true,
            birthDate: true,
            identityDocType: true,
            identitySeries: true,
            identityNumber: true,
            cnp: true,
            email: true,
            phone: true,
        });
        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
            setStep(2);
        }
    };

    const handleClearSignature = () => {
        sigCanvasRef.current?.clear();
        setSignatureError('');
    };

    const handleSubmit = () => {
        if (sigCanvasRef.current?.isEmpty()) {
            setSignatureError('Semnatura este obligatorie');
            return;
        }
        setSignatureError('');
        setIsLoading(true);

        const sourceCanvas = sigCanvasRef.current!.getCanvas();
        const scale = Math.max(window.devicePixelRatio || 1, 2);
        const offscreen = document.createElement('canvas');
        offscreen.width = sourceCanvas.width * scale;
        offscreen.height = sourceCanvas.height * scale;
        const offCtx = offscreen.getContext('2d')!;
        offCtx.drawImage(sourceCanvas, 0, 0, offscreen.width, offscreen.height);
        const signatureBase64 = offscreen.toDataURL('image/png');

        const payload = {
            ...formik.values,
            signatureBase64,
        };

        jwtInstance.post(apiEndpoint, payload)
            .then(response => {
                if (response.status === 200 || response.status === 201) {
                    successResponse(response, 'Inregistrare completa', 'success');
                    setIsLoading(false);
                    setStep(1);
                    sigCanvasRef.current?.clear();
                    onComplete();
                }
            })
            .catch(error => {
                setIsLoading(false);
                errorResponse(error, 'Eroare', 'danger');
            });
    };

    return (
        <>
        <Modal
            isOpen={isOpen}
            setIsOpen={() => {}}
            titleId={'OnboardingWizardModal'}
            isStaticBackdrop
            isScrollable
            isCentered
            size={'lg'}
            fullScreen={undefined}
            isAnimation>
            <ModalHeader className={'mx-2 position-relative'}>
                <CardLabel>
                    <CardTitle style={{fontWeight: 500}}>
                        Semnare Anexa 6 {step === 2 ? '- Semnatura' : ''}
                    </CardTitle>
                </CardLabel>
                <Button
                    color='info'
                    isLight
                    size='sm'
                    icon='Visibility'
                    isDisable={isPreviewLoading}
                    onClick={handlePreview}
                    style={{position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)'}}>
                    {isPreviewLoading ? 'Se încarcă...' : 'Previzualizare'}
                </Button>
            </ModalHeader>
            <ModalBody>
                {step === 1 && (
                    <div className='row g-3'>
                        <div className='col-md-6'>
                            <FormGroup id='lastName' label='Nume' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.lastName}
                                    isTouched={formik.touched.lastName}
                                    invalidFeedback={formik.errors.lastName}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Nume'
                                    disabled={!!userData?.lastName}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='lastName'/>
                        </div>
                        <div className='col-md-6'>
                            <FormGroup id='firstName' label='Prenume' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.firstName}
                                    isTouched={formik.touched.firstName}
                                    invalidFeedback={formik.errors.firstName}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Prenume'
                                    disabled={!!userData?.firstName}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='firstName'/>
                        </div>
                        <div className='col-md-6'>
                            <FormGroup id='domicile' label='Domiciliul in' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.domicile}
                                    isTouched={formik.touched.domicile}
                                    invalidFeedback={formik.errors.domicile}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Localitate'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='domicile'/>
                        </div>
                        <div className='col-md-6'>
                            <FormGroup id='street' label='Strada' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.street}
                                    isTouched={formik.touched.street}
                                    invalidFeedback={formik.errors.street}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Strada'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='street'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='number' label='Nr.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.number}
                                    isTouched={formik.touched.number}
                                    invalidFeedback={formik.errors.number}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Nr.'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='number'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='block' label='Bl.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.block}
                                    isTouched={formik.touched.block}
                                    invalidFeedback={formik.errors.block}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Bl.'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='block'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='staircase' label='Sc.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.staircase}
                                    isTouched={formik.touched.staircase}
                                    invalidFeedback={formik.errors.staircase}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Sc.'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='staircase'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='floor' label='Et.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.floor}
                                    isTouched={formik.touched.floor}
                                    invalidFeedback={formik.errors.floor}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Et.'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='floor'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='apartment' label='Ap.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.apartment}
                                    isTouched={formik.touched.apartment}
                                    invalidFeedback={formik.errors.apartment}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Ap.'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='apartment'/>
                        </div>
                        <div className='col-md-3'>
                            <FormGroup id='sector' label='Sector/Judet' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.sector}
                                    isTouched={formik.touched.sector}
                                    invalidFeedback={formik.errors.sector}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Sector'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='sector'/>
                        </div>
                        <div className='col-md-6'>
                            <FormGroup id='birthDate' label='Data nasterii' className='mb-0'>
                                <Input
                                    type='date'
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.birthDate}
                                    isTouched={formik.touched.birthDate}
                                    invalidFeedback={formik.errors.birthDate}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    disabled={!!userData?.dateOfBirth}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='birthDate'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='identityDocType' label='Tip act de identitate' className='mb-0'>
                                <Select
                                    ariaLabel='Tip act de identitate'
                                    placeholder='Selecteaza...'
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.identityDocType}
                                    isTouched={formik.touched.identityDocType}
                                    invalidFeedback={formik.errors.identityDocType}
                                    isValid={formik.isValid}
                                    isValidMessage={false}>
                                    <Option value='CI'>CI</Option>
                                    <Option value='Pasaport'>Pasaport</Option>
                                </Select>
                            </FormGroup>
                            <ErrorMessage formik={formik} field='identityDocType'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='identitySeries' label='Seria' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.identitySeries}
                                    isTouched={formik.touched.identitySeries}
                                    invalidFeedback={formik.errors.identitySeries}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Seria'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='identitySeries'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='identityNumber' label='Nr.' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.identityNumber}
                                    isTouched={formik.touched.identityNumber}
                                    invalidFeedback={formik.errors.identityNumber}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Numar'
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='identityNumber'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='cnp' label='CNP' className='mb-0'>
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.cnp}
                                    isTouched={formik.touched.cnp}
                                    invalidFeedback={formik.errors.cnp}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='CNP'
                                    disabled={!!userData?.cnp}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='cnp'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='email' label='Email' className='mb-0'>
                                <Input
                                    type='email'
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                    isTouched={formik.touched.email}
                                    invalidFeedback={formik.errors.email}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Email'
                                    disabled={!!userData?.email}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='email'/>
                        </div>
                        <div className='col-md-4'>
                            <FormGroup id='phone' label='Telefon' className='mb-0'>
                                <Input
                                    type='tel'
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.phone}
                                    isTouched={formik.touched.phone}
                                    invalidFeedback={formik.errors.phone}
                                    isValid={formik.isValid}
                                    isValidMessage={false}
                                    placeholder='Telefon'
                                    disabled={!!userData?.phoneNumber}
                                />
                            </FormGroup>
                            <ErrorMessage formik={formik} field='phone'/>
                        </div>

                        {/* Disclaimer */}
                        <div className='col-12 mt-3'>
                            <div
                                className='alert alert-light-info py-2 px-3'
                                role='alert'
                                style={{display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: '0.5rem'}}>
                                <Icon icon='Info' style={{fontSize: '1.25rem', flexShrink: 0, marginTop: '2px'}}/>
                                <span style={{fontSize: 12}}>
                                    Toate informatiile furnizate sunt stocate si gestionate exclusiv pe serverele Victory Cup.
                                    Datele dumneavoastra personale sunt protejate in conformitate cu legislatia in vigoare
                                    privind protectia datelor cu caracter personal (GDPR). Prin completarea acestui formular,
                                    sunteti de acord cu prelucrarea datelor in scopul participarii la competitiile Victory Cup.
                                    Semnatura ta va fi preluata si pe Anexa 1 (componenta echipei), document semnat si de managerul echipei.
                                </span>
                            </div>
                        </div>

                        {/* Next button */}
                        <div className='col-12 d-flex justify-content-end mt-3'>
                            <Button
                                color='primary'
                                onClick={handleGoToSignature}
                                size='sm'>
                                Urmatorul pas
                                <Icon icon='ArrowForward' className='ms-1'/>
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className='row g-3'>
                        <div className='col-12'>
                            <div
                                className='alert alert-light-info py-2 px-3'
                                role='alert'
                                style={{display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: '0.5rem'}}>
                                <Icon icon='Info' style={{fontSize: '1.25rem', flexShrink: 0, marginTop: '2px'}}/>
                                <span style={{fontSize: 12}}>
                                    Va rugam sa semnati in casuta de mai jos folosind mouse-ul sau degetul.
                                    Semnatura va fi atasata documentului Anexa 6.
                                </span>
                            </div>
                        </div>

                        <div className='col-12'>
                            <label className='form-label' style={{fontWeight: 600}}>Semnatura</label>
                            <div
                                style={{
                                    border: signatureError ? '2px solid #dc3545' : '2px solid #dee2e6',
                                    borderRadius: '0.5rem',
                                    backgroundColor: '#fff',
                                    cursor: 'crosshair',
                                }}>
                                <SignatureCanvas
                                    ref={sigCanvasRef}
                                    penColor='black'
                                    minWidth={1.5}
                                    maxWidth={3.5}
                                    velocityFilterWeight={0.7}
                                    canvasProps={{
                                        className: 'w-100',
                                        style: {borderRadius: '0.5rem', height: '250px'},
                                    }}
                                    onBegin={() => setSignatureError('')}
                                />
                            </div>
                            {signatureError && (
                                <div className='text-danger' style={{fontSize: '0.75rem', marginTop: '0.25rem'}}>
                                    {signatureError}
                                </div>
                            )}
                        </div>

                        <div className='col-12 d-flex justify-content-between mt-3'>
                            <div>
                                <Button
                                    color='light'
                                    onClick={() => setStep(1)}
                                    size='sm'>
                                    <Icon icon='ArrowBack' className='me-1'/>
                                    Inapoi
                                </Button>
                            </div>
                            <div className='d-flex gap-2'>
                                <Button
                                    color='warning'
                                    isLight
                                    onClick={handleClearSignature}
                                    size='sm'>
                                    <Icon icon='Refresh' className='me-1'/>
                                    Sterge semnatura
                                </Button>
                                <Button
                                    color='primary'
                                    onClick={handleSubmit}
                                    isDisable={isLoading}
                                    size='sm'>
                                    {isLoading ? 'Se trimite...' : 'Confirma si trimite'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>
        </Modal>

        {showPdfPreview && pdfObjectUrl && (
            <div style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(0,0,0,0.85)',
            }}>
                {/* Header bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.6rem 1rem',
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #dee2e6',
                    flexShrink: 0,
                }}>
                    <span style={{fontWeight: 600, fontSize: '0.95rem'}}>
                        Previzualizare Anexa 6
                    </span>
                    <Button
                        color='light'
                        size='sm'
                        icon='Close'
                        onClick={() => setShowPdfPreview(false)}>
                        Închide
                    </Button>
                </div>

                {/* PDF viewer */}
                <div style={{flex: 1, overflow: 'auto', backgroundColor: '#525659'}}>
                    <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
                        <Viewer fileUrl={pdfObjectUrl} />
                    </Worker>
                </div>
            </div>
        )}
        </>
    );
};

export default OnboardingWizardModal;
